import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, mediumArt } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import TrackRow from "@/components/TrackRow";
import { Play, Pause, Trash2, Music2, Pencil, X, Share2 } from "lucide-react";
import { usePlayer } from "@/contexts/PlayerContext";
import { toast } from "sonner";

const Playlist = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { playTracks, current, playing, togglePlay } = usePlayer();
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const load = () => {
    if (!user) return;
    setLoading(true);
    api.get(`/playlists/${id}`).then((r) => {
      setPl(r.data);
      setName(r.data.name);
      setDesc(r.data.description || "");
    }).catch(() => setPl(null)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  if (!user) {
    return (
      <div data-testid="playlist-page" className="flex flex-col items-center justify-center py-32 text-center">
        <h2 className="font-display text-3xl mb-2">Sign in to view playlists</h2>
        <button onClick={login} className="mt-4 bg-white text-black rounded-full px-6 py-2.5 font-semibold">Sign in</button>
      </div>
    );
  }

  if (loading) return <div className="text-white/40">Loading playlist...</div>;
  if (!pl) return <div className="text-white/60">Playlist not found.</div>;

  const tracks = pl.tracks || [];
  const playable = tracks.filter((t) => t.previewUrl);
  const isPlPlaying = playing && playable.some((t) => t.trackId === current?.trackId);
  const cover = tracks[0]?.artworkUrl100;

  const save = async () => {
    try {
      const r = await api.patch(`/playlists/${id}`, { name, description: desc });
      setPl(r.data);
      setEditing(false);
      toast.success("Playlist updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (trackId) => {
    try {
      const r = await api.delete(`/playlists/${id}/tracks/${trackId}`);
      setPl(r.data);
    } catch {
      toast.error("Failed to remove");
    }
  };

  const deletePl = async () => {
    if (!window.confirm("Delete this playlist?")) return;
    try {
      await api.delete(`/playlists/${id}`);
      toast.success("Playlist deleted");
      navigate("/library");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="playlist-page" className="pb-16">
      <div className="relative -mx-4 sm:-mx-8 -mt-8 px-4 sm:px-8 py-10 mb-8 overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background: `radial-gradient(800px circle at 30% 30%, rgba(255,51,102,0.4), transparent 60%), radial-gradient(700px circle at 80% 70%, rgba(157,76,221,0.3), transparent 60%), #050505`,
          }}
        />
        <div className="flex flex-col sm:flex-row gap-6 sm:items-end">
          <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-lg shadow-2xl overflow-hidden bg-gradient-to-br from-[#FF2E00] via-[#FF2E00] to-[#E2FF31] flex items-center justify-center">
            {cover ? (
              <img src={mediumArt(cover)} alt="" className="w-full h-full object-cover" />
            ) : (
              <Music2 size={64} className="text-black/60" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-2">Playlist</p>
            {editing ? (
              <div className="space-y-3">
                <input
                  data-testid="playlist-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-2xl font-display font-semibold outline-none focus:border-[#FF2E00]"
                />
                <textarea
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  placeholder="Add a description..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 outline-none focus:border-[#FF2E00] text-sm"
                  rows={2}
                />
                <div className="flex gap-2">
                  <button data-testid="playlist-save-btn" onClick={save} className="bg-white text-black rounded-full px-5 py-2 font-semibold">Save</button>
                  <button onClick={() => { setEditing(false); setName(pl.name); setDesc(pl.description || ""); }} className="text-white/70 hover:text-white px-3"><X size={18} /></button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-none break-words">{pl.name}</h1>
                {pl.description && <p className="text-white/60 mt-3">{pl.description}</p>}
                <p className="text-sm text-white/50 mt-3">{tracks.length} songs</p>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button
            data-testid="playlist-play-btn"
            onClick={() => {
              if (playable.length === 0) return;
              if (isPlPlaying) togglePlay();
              else playTracks(playable, 0);
            }}
            disabled={playable.length === 0}
            className="w-14 h-14 rounded-full bg-[#FF2E00] text-white flex items-center justify-center hover:scale-105 hover:shadow-[0_0_24px_rgba(255,51,102,0.5)] transition-all disabled:opacity-30"
          >
            {isPlPlaying ? <Pause size={22} fill="currentColor" /> : <Play size={22} fill="currentColor" className="ml-1" />}
          </button>
          {!editing && (
            <button data-testid="playlist-edit-btn" onClick={() => setEditing(true)} className="p-3 rounded-full bg-white/5 hover:bg-white/15 transition">
              <Pencil size={16} />
            </button>
          )}
          <button
            data-testid="playlist-share-btn"
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/p/${id}`;
              navigator.clipboard?.writeText(url).then(
                () => toast.success("Share link copied to clipboard!", { description: url }),
                () => toast.error("Could not copy link"),
              );
            }}
            className="p-3 rounded-full bg-white/5 hover:bg-white/15 transition"
            title="Copy public share link"
          >
            <Share2 size={16} />
          </button>
          <button data-testid="playlist-delete-btn" onClick={deletePl} className="p-3 rounded-full bg-white/5 hover:bg-white/15 hover:text-[#FF2E00] transition">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {tracks.length === 0 ? (
        <div className="text-center py-16 text-white/50">
          <Music2 size={40} className="mx-auto mb-3 text-white/20" />
          <p>This playlist is empty.</p>
          <p className="text-sm mt-2 text-white/40">Add songs using the menu (⋯) next to any track.</p>
        </div>
      ) : (
        <div className="flex flex-col">
          {tracks.map((t, i) => (
            <div key={`${t.trackId}-${i}`} className="relative">
              <TrackRow track={t} index={i} queue={playable} />
              <button
                onClick={() => remove(t.trackId)}
                data-testid={`playlist-remove-${t.trackId}`}
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 hover:opacity-100 p-2 text-white/50 hover:text-[#FF2E00]"
                title="Remove from playlist"
              >
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Playlist;
